import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Loader2, Wallet, ChevronDown, ChevronRight, Download, TrendingUp, TrendingDown } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function Trend({ current, prev, label, dark }) {
  if (prev == null || prev === 0) return null;
  const diff = (current || 0) - prev;
  const pct = ((diff / prev) * 100).toFixed(1);
  if (Math.abs(diff) < 1) {
    return <div className={`text-[10px] font-sans font-normal ${dark ? 'text-slate-400' : 'text-muted-foreground'}`}>= {label}</div>;
  }
  const up = diff > 0;
  const cls = dark ? (up ? 'text-emerald-300' : 'text-red-300') : (up ? 'text-emerald-600' : 'text-red-500');
  return (
    <div className={`text-[10px] font-sans font-normal inline-flex items-center gap-0.5 justify-end w-full ${cls}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{pct}% vs {label}
    </div>
  );
}

function EmpRows({ vert, vKey }) {
  const rows = [];
  for (let k = 0; k < (vert.entries || []).length; k++) {
    const e = vert.entries[k];
    rows.push(
      <TableRow key={vKey + '-emp-' + e.employee_id} className="text-xs bg-slate-50/60 dark:bg-slate-900/30" data-testid={'payout-emp-' + e.employee_id}>
        <TableCell className="pl-16 py-1.5">
          <span className="text-muted-foreground">•</span>
          <span className="ml-2">{e.employee_name}</span>
          {e.employee_code && <span className="ml-1.5 font-mono text-muted-foreground">({e.employee_code})</span>}
        </TableCell>
        <TableCell className="py-1.5"></TableCell>
        <TableCell className="text-right font-mono py-1.5">{fmt(e.net_payable)}</TableCell>
        <TableCell className="text-right font-mono py-1.5">{fmt(e.bank_transfer)}</TableCell>
        <TableCell className="text-right py-1.5">
          {e.is_paid
            ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">✓ Paid</Badge>
            : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Unpaid</Badge>}
        </TableCell>
      </TableRow>
    );
  }
  return rows;
}

function VertRows({ org, openVerts, onToggleVert }) {
  const rows = [];
  for (let i = 0; i < org.verticals.length; i++) {
    const v = org.verticals[i];
    const vKey = org.organization_id + '::' + v.vertical_name;
    const isOpen = openVerts[vKey];
    rows.push(
      <TableRow
        key={vKey}
        className="text-sm cursor-pointer hover:bg-muted/40"
        onClick={() => onToggleVert(vKey)}
        data-testid={'payout-vert-' + (v.vertical_id || 'none')}
      >
        <TableCell className="pl-10 text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {v.vertical_name}
          </span>
        </TableCell>
        <TableCell className="text-right">{v.employees}</TableCell>
        <TableCell className="text-right font-mono">{fmt(v.net_payable)}</TableCell>
        <TableCell className="text-right font-mono">{fmt(v.bank_transfer)}</TableCell>
        <TableCell className="text-right font-mono">
          {fmt(v.paid_amount)}
          <span className="text-[10px] text-muted-foreground ml-1.5">({v.paid_count}/{v.employees} paid)</span>
        </TableCell>
      </TableRow>
    );
    if (isOpen) {
      rows.push(<EmpRows key={vKey + '-emps'} vert={v} vKey={vKey} />);
    }
  }
  return rows;
}

function applyVerticalFilter(orgs, vertName) {
  if (vertName === 'all') return orgs;
  const out = [];
  for (let i = 0; i < orgs.length; i++) {
    const verts = orgs[i].verticals.filter(function(v) { return v.vertical_name === vertName; });
    if (verts.length === 0) continue;
    const o = { ...orgs[i], verticals: verts, employees: 0, net_payable: 0, bank_transfer: 0, paid_amount: 0, paid_count: 0 };
    for (let j = 0; j < verts.length; j++) {
      o.employees += verts[j].employees;
      o.net_payable += verts[j].net_payable;
      o.bank_transfer += verts[j].bank_transfer;
      o.paid_amount += verts[j].paid_amount;
      o.paid_count += verts[j].paid_count;
    }
    out.push(o);
  }
  return out;
}

function buildPayoutCSV(orgs, totals, monthLabel) {
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [['Organization', 'Vertical', 'Employee', 'Code', 'Net Payable', 'Bank Transfer', 'Paid Amount', 'Status'].join(',')];
  for (let i = 0; i < orgs.length; i++) {
    const org = orgs[i];
    lines.push([esc(org.organization_name), '', '', '', org.net_payable || 0, org.bank_transfer || 0, org.paid_amount || 0, `${org.paid_count}/${org.employees} paid`].join(','));
    for (let j = 0; j < org.verticals.length; j++) {
      const v = org.verticals[j];
      lines.push([esc(org.organization_name), esc(v.vertical_name), '', '', v.net_payable || 0, v.bank_transfer || 0, v.paid_amount || 0, `${v.paid_count}/${v.employees} paid`].join(','));
      for (let k = 0; k < (v.entries || []).length; k++) {
        const e = v.entries[k];
        lines.push([esc(org.organization_name), esc(v.vertical_name), esc(e.employee_name), esc(e.employee_code), e.net_payable || 0, e.bank_transfer || 0, e.is_paid ? (e.net_payable || 0) : 0, e.is_paid ? 'Paid' : 'Unpaid'].join(','));
      }
    }
  }
  lines.push(['Grand Total — ' + monthLabel, '', '', '', totals.net_payable || 0, totals.bank_transfer || 0, totals.paid_amount || 0, `${totals.paid_count}/${totals.employees} paid`].join(','));
  return lines.join('\n');
}

function sumTotals(orgs) {
  const t = { employees: 0, net_payable: 0, bank_transfer: 0, paid_amount: 0, paid_count: 0 };
  for (let i = 0; i < orgs.length; i++) {
    t.employees += orgs[i].employees;
    t.net_payable += orgs[i].net_payable;
    t.bank_transfer += orgs[i].bank_transfer;
    t.paid_amount += orgs[i].paid_amount;
    t.paid_count += orgs[i].paid_count;
  }
  return t;
}

export default function SalaryPayoutTable() {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [openVerts, setOpenVerts] = useState({});
  const [verticalFilter, setVerticalFilter] = useState('all');

  const pm = parseInt(month) === 1 ? 12 : parseInt(month) - 1;
  const py = parseInt(month) === 1 ? parseInt(year) - 1 : parseInt(year);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/dashboard/salary-payout', { params: { month, year } }),
      api.get('/dashboard/salary-payout', { params: { month: pm, year: py } }).catch(() => null),
    ])
      .then(([cur, prev]) => {
        setData(cur.data);
        setPrevData(prev ? prev.data : null);
      })
      .catch(() => { setData(null); setPrevData(null); })
      .finally(() => setLoading(false));
  }, [month, year, pm, py]);

  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleVert = (key) => setOpenVerts(prev => ({ ...prev, [key]: !prev[key] }));
  const allOrgs = (data && data.organizations) || [];
  const vertOptions = [];
  for (let i = 0; i < allOrgs.length; i++) {
    for (let j = 0; j < allOrgs[i].verticals.length; j++) {
      const name = allOrgs[i].verticals[j].vertical_name;
      if (vertOptions.indexOf(name) === -1) vertOptions.push(name);
    }
  }
  vertOptions.sort();
  const orgList = applyVerticalFilter(allOrgs, verticalFilter);
  const totals = verticalFilter === 'all' ? ((data && data.totals) || {}) : sumTotals(orgList);

  const prevOrgList = applyVerticalFilter((prevData && prevData.organizations) || [], verticalFilter);
  const prevTotals = verticalFilter === 'all' ? ((prevData && prevData.totals) || {}) : sumTotals(prevOrgList);
  const prevByOrg = {};
  for (let i = 0; i < prevOrgList.length; i++) {
    prevByOrg[prevOrgList[i].organization_id] = prevOrgList[i].net_payable;
  }
  const prevLabel = MONTHS[pm - 1].slice(0, 3) + (py !== parseInt(year) ? ' ' + py : '');

  const handleExportCSV = () => {
    const monthLabel = MONTHS[parseInt(month) - 1] + ' ' + year;
    const csv = buildPayoutCSV(orgList, totals, monthLabel);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary-payout-${MONTHS[parseInt(month) - 1].toLowerCase()}-${year}${verticalFilter !== 'all' ? '-' + verticalFilter.toLowerCase().replace(/\s+/g, '-') : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="salary-payout-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="h-5 w-5" />Monthly Salary Payout — by Organization &amp; Vertical
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              From salary entries of {MONTHS[parseInt(month) - 1]} {year}
              {data ? ` · ${totals.employees || 0} entries (${totals.paid_count || 0} paid) of ${data.active_employees} active employees` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={verticalFilter} onValueChange={setVerticalFilter}>
              <SelectTrigger className="w-44 h-9" data-testid="payout-vertical-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All Verticals</SelectItem>
                {vertOptions.map(function(v) { return <SelectItem key={v} value={v}>{v}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-32 h-9" data-testid="payout-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map(function(m, i) { return <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24 h-9" data-testid="payout-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map(function(y) { return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9" onClick={handleExportCSV} disabled={loading || orgList.length === 0} data-testid="payout-export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : orgList.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground" data-testid="payout-empty">
            No salary entries found for {MONTHS[parseInt(month) - 1]} {year}{verticalFilter !== 'all' ? ` in "${verticalFilter}"` : ''}.
          </div>
        ) : (
          <Table data-testid="payout-table">
            <TableHeader>
              <TableRow>
                <TableHead>Organization / Vertical</TableHead>
                <TableHead className="text-right">Employees</TableHead>
                <TableHead className="text-right">Net Payable</TableHead>
                <TableHead className="text-right">Bank Transfer</TableHead>
                <TableHead className="text-right">Paid Salary ✓</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgList.map(function(org) {
                const isCollapsed = collapsed[org.organization_id];
                const orgRow = (
                  <TableRow
                    key={org.organization_id}
                    className="bg-muted/60 font-semibold cursor-pointer hover:bg-muted"
                    onClick={() => toggle(org.organization_id)}
                    data-testid={'payout-org-' + org.organization_id}
                  >
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        {org.organization_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{org.employees}</TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt(org.net_payable)}
                      <Trend current={org.net_payable} prev={prevByOrg[org.organization_id]} label={prevLabel} />
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmt(org.bank_transfer)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt(org.paid_amount)}
                      <Badge variant="outline" className={'ml-2 text-[10px] ' + (org.paid_count === org.employees ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200')}>
                        {org.paid_count}/{org.employees}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
                return (
                  <React.Fragment key={org.organization_id}>
                    {orgRow}
                    {!isCollapsed && <VertRows org={org} openVerts={openVerts} onToggleVert={toggleVert} />}
                  </React.Fragment>
                );
              })}
              <TableRow className="bg-slate-900 hover:bg-slate-900 dark:bg-slate-800">
                <TableCell className="text-white font-bold">Grand Total — {MONTHS[parseInt(month) - 1]} {year}</TableCell>
                <TableCell className="text-right text-white font-bold">{totals.employees}</TableCell>
                <TableCell className="text-right font-mono text-emerald-300 font-bold">
                  {fmt(totals.net_payable)}
                  <Trend current={totals.net_payable} prev={prevTotals.net_payable} label={prevLabel} dark />
                </TableCell>
                <TableCell className="text-right font-mono text-emerald-300 font-bold">{fmt(totals.bank_transfer)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-300 font-bold" data-testid="payout-total-paid">{fmt(totals.paid_amount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
